import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Container } from '../ui/Primitives';
import { PRESS_MENTIONS } from '../../data/press';
import type { PressMention } from '../../data/press';
const INTERVAL_MS = 6000;

/** The outlet's own logo where we hold it, otherwise a wordmark in its brand colour. */
function OutletMark({
  mention,
  size = 'lg'
}: {
  mention: PressMention;
  size?: 'sm' | 'lg';
}) {
  if (mention.logoUrl) {
    return <img src={mention.logoUrl} alt={mention.outlet} loading="lazy" className={size === 'lg' ? 'h-8 w-auto' : 'h-5 w-auto'} data-id="element-381" data-mp="1owlgyz6antpi" />;
  }
  return <span className={`font-display leading-none ${size === 'lg' ? 'text-[26px] sm:text-[30px]' : 'text-[15px]'}`} style={{
    color: mention.brandColour
  }} data-id="element-382" data-mp="1xw06nwen008y">
      {mention.outlet}
    </span>;
}

/**
 * Press credibility band, as a slideshow.
 *
 * It advances on its own so the breadth of coverage reads without interaction,
 * but pauses on hover and on focus so nobody loses a headline mid read, and it
 * becomes a static list under prefers-reduced-motion.
 */
export function PressStrip() {
  const reduced = useReducedMotion();
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const total = PRESS_MENTIONS.length;
  React.useEffect(() => {
    if (reduced || paused || total < 2) return undefined;
    const timer = window.setInterval(() => setIndex(current => (current + 1) % total), INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [reduced, paused, total]);
  if (total === 0) return null;
  const mention = PRESS_MENTIONS[index];
  const go = (next: number) => setIndex((next + total) % total);
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(index + 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(index - 1);
    }
  };
  return <section aria-labelledby="press" className="border-y border-line bg-mist" data-id="element-383" data-mp="1gq62auijndv4">
      <Container className="py-12 lg:py-14" data-id="element-384" data-mp="1gs956f4sp1h9">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-385" data-mp="1uqqmnv43xhqq">
          <h2 id="press" className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold" data-id="element-386" data-mp="1qoc6xjvqiy3o">
            Featured in
          </h2>

          {/* Outlet row, doubling as the slideshow's navigation */}
          <ul className="flex flex-wrap items-center gap-x-7 gap-y-3" data-id="element-387" data-mp="1zpk1prz1lgt0">
            {PRESS_MENTIONS.map((item, itemIndex) => <li key={item.outlet} data-id="element-388" data-mp="2ed53nik6l9zc" data-mpi={itemIndex}>
                <button type="button" onClick={() => setIndex(itemIndex)} aria-current={itemIndex === index} className={`transition-opacity duration-200 ${itemIndex === index ? 'opacity-100' : 'opacity-45 hover:opacity-80'}`} data-id="element-389" data-mp="29elgjetups5e">
                  <OutletMark mention={item} size="sm" data-id="element-390" data-mp="322czrmrml3jj" />
                  <span className="sr-only" data-id="element-391" data-mp="36zwf6q027md2">, show this coverage</span>
                </button>
              </li>)}
          </ul>
        </div>

        <div className="mt-8" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} onKeyDown={onKeyDown} role="group" aria-roledescription="carousel" aria-label="Press coverage" tabIndex={-1} data-id="element-392" data-mp="2t03txa7axych">
          <div className="relative min-h-[188px] sm:min-h-[168px]" data-id="element-393" data-mp="29dz2xac9wd9k">
            <AnimatePresence mode="wait" initial={false} data-id="element-394" data-mp="2eapjdyrzpavi">
              <motion.article key={mention.outlet} initial={reduced ? false : {
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduced ? undefined : {
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.28,
              ease: [0.23, 1, 0.32, 1]
            }} aria-live="polite" className="rounded-md border border-line bg-panel p-6 sm:p-8" style={{
              borderLeft: `3px solid ${mention.brandColour}`
            }} data-id="element-395" data-mp="2e94mw3m47hv0">
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-396" data-mp="1jiygr24lhm25">
                  <OutletMark mention={mention} data-id="element-397" data-mp="1jgvmu70bn8s5" />
                  <span className="text-small text-muted" data-id="element-398" data-mp="1ekn6iqae19je">
                    {new Date(mention.publishedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                  </span>
                </div>

                <h3 className="mt-5 max-w-3xl font-display text-[21px] leading-snug text-ink sm:text-[25px]" data-id="element-399" data-mp="10lnd75bxk6gw">
                  {mention.url ? <a href={mention.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-start gap-2 underline-offset-4 hover:underline" data-id="element-400" data-mp="2uxbz5rgkx9as">
                      {mention.headline}
                      <ArrowUpRightIcon className="mt-1.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-401" data-mp="qfnmj0vkd6ip" />
                      <span className="sr-only" data-id="element-402" data-mp="ugfu7posf3cv">, opens in a new tab</span>
                    </a> : mention.headline}
                </h3>

                <p className="mt-3 max-w-prose text-[16px] text-body" data-id="element-403" data-mp="2rco04dcbixl">{mention.excerpt}</p>
              </motion.article>
            </AnimatePresence>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4" data-id="element-404" data-mp="bqrf961bzxj5">
            <p className="text-small text-muted" aria-hidden="true" data-id="element-405" data-mp="bowupstz27fl">
              {index + 1} of {total}
            </p>
            <div className="flex items-center gap-2" data-id="element-406" data-mp="1n5wiqc9ae03t">
              <button type="button" onClick={() => go(index - 1)} aria-label="Previous coverage" className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink transition-colors duration-200 hover:border-gold hover:text-gold" data-id="element-407" data-mp="1n4lfcdjr6ud8">
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-408" data-mp="3o8e5l11c2vs1" />
              </button>
              <button type="button" onClick={() => go(index + 1)} aria-label="Next coverage" className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink transition-colors duration-200 hover:border-gold hover:text-gold" data-id="element-409" data-mp="30itaxkvtia2x">
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-410" data-mp="35hmmv8vght0z" />
              </button>
            </div>
          </div>
        </div>
      </Container>
    </section>;
}